import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Flex, 
  Card, 
  CardHeader, 
  CardBody, 
  Heading, 
  Text, 
  VStack, 
  HStack, 
  Button, 
  Tag, 
  TagLeftIcon, 
  TagLabel, 
  IconButton, 
  Badge, 
  Slider, 
  SliderTrack, 
  SliderFilledTrack, 
  SliderThumb, 
  Image as ChakraImage, // Alias the import
  AspectRatio, 
  useToast,
  useColorModeValue,
  useColorMode,
  useBreakpointValue,
  Tooltip,
  Divider
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { 
  Robot, 
  Target, 
  TagSimple, 
  Crosshair, 
  Trash, 
  Plus 
} from '@phosphor-icons/react';
import MediaViewer from './MediaViewer';
import { useMedia } from '../../hooks/useMedia';
import { useSublocations } from '../../hooks/useSublocations';

const MediaPreviewSection = ({
  imageSrc,
  displaySize,
  naturalSize,
  detections: initialDetections,
  selectedFeatures,
  isUploaded,
  autoBadgeBg,
  handleImageLoad, // Note: This prop might not be used if MediaViewer handles its own load
  handleFeatureToggle, // Note: This prop might not be used if MediaViewer handles toggles internally
  onAddFeatureClick,
  onNewDetection,
  onStartLabelingFeature,
  onDeleteFeature: parentOnDeleteFeature,
  mediaId,
  siteId,
  setDetections, // Note: Check if MediaViewer needs this directly or if parent handles state
  accentColor,
  confidenceThreshold,
  onConfidenceChange
}) => {
  // Get sublocation data if media has subLocationId
  const { sublocations } = useSublocations(siteId);
  const [activeFeature, setActiveFeature] = useState(null);
  const [localDetections, setLocalDetections] = useState(initialDetections || []);
  const [filteredDetections, setFilteredDetections] = useState([]);
  const [imageRatio, setImageRatio] = useState(16/9);
  const { refreshMedia } = useMedia();
  const toast = useToast();
  const { colorMode } = useColorMode();

  // Responsive layout
  const sidebarWidth = useBreakpointValue({ base: "100%", md: "320px" });
  const iconSize = useBreakpointValue({ base: 16, md: 18 });
  const buttonSize = useBreakpointValue({ base: "xs", md: "sm" });
  const isMobile = useBreakpointValue({ base: true, md: false }); 
  
  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'gray.300');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const tagBg = useColorModeValue('gray.100', 'gray.700');
  const tagHoverBg = useColorModeValue('gray.200', 'gray.600');
  const manualTagBg = useColorModeValue('blue.50', 'blue.900');
  const sliderTrackBg = useColorModeValue('gray.200', 'gray.600');
  const sliderThumbBg = useColorModeValue('blue.500', 'blue.300');

  useEffect(() => {
    setLocalDetections(initialDetections || []);
  }, [initialDetections]);

  useEffect(() => {
    // Filter detections based on confidence threshold
    const filtered = localDetections.filter(detection => {
      // Always show manual detections
      if (detection.manual) return true;
      // Filter auto-detected features based on confidence
      return detection.confidence >= confidenceThreshold;
    });
    setFilteredDetections(filtered);
  }, [localDetections, confidenceThreshold]);

  useEffect(() => {
    // Update aspect ratio when natural size changes
    // This is mainly for the desktop AspectRatio component
    if (naturalSize?.width && naturalSize?.height) {
      setImageRatio(naturalSize.width / naturalSize.height);
    }
  }, [naturalSize]);

  const handleLabelInImage = (feature, key) => {
    setActiveFeature({ ...feature, key });
    // Ensure onStartLabelingFeature is called if needed by parent
    if (onStartLabelingFeature) {
      onStartLabelingFeature(key);
    }
  };

  // This function now primarily manages local state, 
  // but still calls the parent function if provided.
  const handleDeleteFeature = async (index) => {
    const deletedDetection = localDetections[index];
    if (!deletedDetection) return;

    const updatedDetections = localDetections.filter((_, i) => i !== index);
    setLocalDetections(updatedDetections); // Update local state first

    // If before upload, update parent's detections state if prop exists
    if (!isUploaded && setDetections) {
      setDetections(updatedDetections);
    }

    // Notify parent for potential side effects (like DB update)
    if (parentOnDeleteFeature) {
      try {
        await parentOnDeleteFeature(index); // Call parent's delete logic
        // Refresh logic might be handled by parent or here
        if (mediaId) {
          await refreshMedia(mediaId); 
          toast({
            title: "Feature deleted",
            description: "The feature was successfully removed",
            status: "success",
            duration: 2000
          });
        }
      } catch (error) {
        console.error('Error calling parent onDeleteFeature:', error);
        toast({
          title: "Error",
          description: "Failed to fully process feature deletion",
          status: "error",
          duration: 3000
        });
        // Optionally revert local state if parent call fails critically
        // setLocalDetections(localDetections); 
      }
    }
  };

  // Similar pattern for adding detections
  const handleNewDetection = async (detection) => {
     // Update local state optimistically or after parent confirms
     // setLocalDetections(prev => [...prev, detection]); // Example optimistic update

    if (onNewDetection) {
      try {
        await onNewDetection(detection); // Call parent's add logic
        // Parent might update props, or we refresh here
        if (mediaId) {
          await refreshMedia(mediaId); 
          toast({
            title: "Feature added",
            description: "The feature was successfully added",
            status: "success",
            duration: 2000
          });
        }
         // Update local state after parent confirmation if not optimistic
         setLocalDetections(prev => [...prev, detection]); 
      } catch (error) {
        console.error('Error calling parent onNewDetection:', error);
        toast({
          title: "Error",
          description: "Failed to add feature",
          status: "error",
          duration: 3000
        });
      }
    } else {
       // If no parent handler, just update local state
       setLocalDetections(prev => [...prev, detection]);
    }
  };

  // Group features based on the local filtered state
  const groupedFeatures = {
    autoDetected: filteredDetections
      .filter(d => !d.manual)
      .map((d, index) => ({
        key: `${d.class}_${index}_auto`, // Ensure unique key
        label: d.class,
        confidence: d.confidence,
        autoDetected: true,
        // Find original index in localDetections for deletion
        index: localDetections.findIndex(det => det === d) 
      })),
    manual: filteredDetections
      .filter(d => d.manual)
      .map((d, index) => ({
        key: `${d.class}_${index}_manual`, // Ensure unique key
        label: selectedFeatures?.[d.class]?.label || d.class, // Use optional chaining
        confidence: d.confidence,
        autoDetected: false,
         // Find original index in localDetections for deletion
        index: localDetections.findIndex(det => det === d)
      }))
  };

  // Format feature label for display
  const formatFeatureLabel = (label) => {
    if (!label) return 'Unknown Feature';
    return label
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Prepare props for MediaViewer consistently
  const mediaViewerProps = {
    media: {
      url: imageSrc,
      type: 'image',
      predictions: filteredDetections.map(d => ({
        class: d.class,
        source: d.manual ? 'manual' : 'auto',
        confidence: d.confidence || 0.99,
        x: d.x,
        y: d.y,
        width: d.width,
        height: d.height,
        subLocationId: d.subLocationId
      })),
      siteLocation: siteId?.siteLocation, // Pass site location if available
      subLocationId: localDetections[0]?.subLocationId // Pass sublocation if available
    },
    subLocation: localDetections[0]?.subLocationId ? 
      sublocations.find(s => s.id === localDetections[0].subLocationId) : null,
    showFeatureTags: true, // Always show tags/boxes if available
    onFeatureTagsToggle: () => {}, // Provide dummy function if needed
    onUpdateMedia: refreshMedia, // Pass refresh function
    onBoxDrawn: handleNewDetection, // Use internal handler
    isDrawingMode: !!activeFeature,
    onAddFeature: onAddFeatureClick, // Pass parent handler
    onDeleteFeature: handleDeleteFeature, // Use internal handler
    hideDetectionsList: true, // Keep hiding the internal list
    mediaId: mediaId,
    siteId: siteId,
  };

  return (
    <Flex 
      direction={{ base: "column", md: "row" }} 
      gap={6}
      align="flex-start"
    >
      {/* Conditionally render based on mobile */}
      {imageSrc && (
        isMobile ? (
          // Mobile: Render MediaViewer directly in a Box, NO AspectRatio
          <Box
            flexShrink={0} // Prevent shrinking
            width="100%" // Full width
            minH={{ base: "300px" }} // Minimum height
            maxH={{ base: "55vh" }} // Constrain max height on mobile
            borderRadius="md"
            borderWidth="1px"
            borderColor={cardBorderColor}
            bg={cardBg} // Use standard background
            boxShadow="sm"
            position="relative" // Needed for MediaViewer overlays
            display="flex" // Use flex to center MediaViewer if it's smaller than the box
            alignItems="center"
            justifyContent="center"
            overflow="hidden" // Clip content if MediaViewer overflows
          >
            <MediaViewer {...mediaViewerProps} />
          </Box>
        ) : (
          // Desktop: Keep original structure with AspectRatio
          <Box 
            flex={1}
            // minH removed, AspectRatio handles height
            borderRadius="md"
            borderWidth="1px"
            borderColor={cardBorderColor}
            bg={cardBg}
            boxShadow="sm"
          >
            <AspectRatio 
              ratio={imageRatio} 
              width="100%"
              // height="auto" // Let ratio dictate height
              maxW="100%" 
              maxH={{ md: "calc(100vh - 200px)" }} // Max height constraint
              overflow="hidden" 
              margin="0 auto" 
              // Centering props might not be needed if child fills aspect ratio
              // display="flex" 
              // alignItems="center" 
              // justifyContent="center" 
            >
              {/* MediaViewer should fill the AspectRatio */}
              <MediaViewer {...mediaViewerProps} />
            </AspectRatio>
          </Box>
        )
      )}

      {/* Sidebar for Feature Editing (Only shown before upload) */}
      {imageSrc && !isUploaded && (
        <Box 
          width={sidebarWidth} 
          flexGrow={1} // Allow sidebar to take remaining space
          overflowY="auto" // Make sidebar scrollable if content overflows
          pb={{ base: 4, md: 0 }} // Add padding at the bottom for scroll UX on mobile
        >
          <Card 
            variant="outline" 
            bg={cardBg} 
            borderColor={cardBorderColor}
            boxShadow="sm"
            borderRadius="md"
          >
            <CardHeader pb={2}>
              <Flex align="center" gap={2} mb={2}>
                <TagSimple size={iconSize} color={accentColor || 'blue.500'} />
                <Heading size="sm" color={headingColor}>Image Features</Heading>
              </Flex>
              
              <Text fontSize="sm" color={textColor} mb={4}>
                Review or add features in this image.
              </Text>
              
              {/* Confidence Slider */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  Detection Confidence: {Math.round(confidenceThreshold * 100)}%
                </Text>
                <Slider
                  aria-label="detection-confidence"
                  defaultValue={0.5}
                  min={0.1}
                  max={0.9}
                  step={0.1}
                  value={confidenceThreshold}
                  onChange={onConfidenceChange} // Ensure this prop is passed from parent
                  width="100%"
                  colorScheme="blue"
                >
                  <SliderTrack bg={sliderTrackBg}>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb bg={sliderThumbBg} />
                </Slider>
              </Box>
            </CardHeader>
            
            <CardBody>
              <VStack align="stretch" spacing={4}>
                {/* Add Features Button */}
                <Button
                  leftIcon={<Plus weight="bold" />}
                  onClick={onAddFeatureClick} // Ensure this prop is passed from parent
                  size={buttonSize}
                  colorScheme="blue"
                  width="100%"
                  borderRadius="md"
                  transition="all 0.2s"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'md'
                  }}
                >
                  Add Feature
                </Button>

                {/* Auto-detected features */}
                {groupedFeatures.autoDetected.length > 0 && (
                  <Box>
                    <Flex align="center" gap={2} mb={2}>
                      <Robot size={iconSize} color={accentColor || 'purple.500'} />
                      <Text fontWeight="bold" fontSize="sm" color={headingColor}>
                        Auto-detected Features
                      </Text>
                    </Flex>
                    <VStack align="stretch" spacing={2}>
                      {groupedFeatures.autoDetected.map((feature) => ( // Removed index from map args
                        <Flex 
                          key={feature.key} 
                          justify="space-between" 
                          align="center"
                          p={2}
                          borderRadius="md"
                          bg={tagBg}
                          _hover={{ bg: tagHoverBg }}
                          transition="all 0.2s"
                        >
                          <HStack spacing={2} flex={1} overflow="hidden">
                             <Badge 
                              colorScheme="purple" 
                              borderRadius="full"
                              px={2}
                              flexShrink={0}
                            >
                              {/* Maybe don't need index number? */}
                            </Badge>
                            <Text fontSize="sm" fontWeight="medium" isTruncated>
                              {formatFeatureLabel(feature.label)}
                            </Text>
                          </HStack>
                          <HStack spacing={1}>
                            {feature.confidence && (
                              <Badge colorScheme="green" variant="subtle">
                                {Math.round(feature.confidence * 100)}%
                              </Badge>
                            )}
                            <Tooltip label="Delete feature">
                              <IconButton
                                icon={<Trash weight="bold" />}
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                aria-label="Delete feature"
                                onClick={() => handleDeleteFeature(feature.index)} // Use internal handler
                              />
                            </Tooltip>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Manually added features */}
                {groupedFeatures.manual.length > 0 && (
                  <Box>
                    <Flex align="center" gap={2} mb={2}>
                      <Crosshair size={iconSize} color={accentColor || 'blue.500'} />
                      <Text fontWeight="bold" fontSize="sm" color={headingColor}>
                        Added Features
                      </Text>
                    </Flex>
                    <VStack align="stretch" spacing={2}>
                      {groupedFeatures.manual.map((feature) => ( // Removed index from map args
                        <Flex 
                          key={feature.key} 
                          justify="space-between" 
                          align="center"
                          p={2}
                          borderRadius="md"
                          bg={manualTagBg}
                          _hover={{ bg: useColorModeValue('blue.100', 'blue.800') }}
                          transition="all 0.2s"
                        >
                           <HStack spacing={2} flex={1} overflow="hidden">
                             <Badge 
                              colorScheme="blue" 
                              borderRadius="full"
                              px={2}
                              flexShrink={0}
                            >
                               {/* Maybe don't need index number? */}
                            </Badge>
                            <Text fontSize="sm" fontWeight="medium" isTruncated>
                              {formatFeatureLabel(feature.label || feature.key)}
                            </Text>
                          </HStack>
                          <HStack spacing={1}>
                            {/* Relabel might need onStartLabelingFeature passed down */}
                            <Tooltip label="Relabel feature (Draw New Box)">
                              <IconButton
                                icon={<Target weight="bold" />}
                                size="xs"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleLabelInImage(feature, feature.key)} // Use internal handler
                                isDisabled={!!activeFeature}
                                aria-label="Relabel feature"
                              />
                            </Tooltip>
                            <Tooltip label="Delete feature">
                              <IconButton
                                icon={<Trash weight="bold" />}
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                aria-label="Delete feature"
                                onClick={() => handleDeleteFeature(feature.index)} // Use internal handler
                              />
                            </Tooltip>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* No Features Message */}
                {groupedFeatures.autoDetected.length === 0 && groupedFeatures.manual.length === 0 && (
                  <Flex 
                    direction="column" 
                    align="center" 
                    justify="center" 
                    py={8} 
                    px={4}
                    borderWidth="1px"
                    borderStyle="dashed"
                    borderColor={cardBorderColor}
                    borderRadius="md"
                    textAlign="center"
                  >
                    <TagSimple size={24} color={textColor} weight="light" />
                    <Text color={textColor} fontSize="sm" mt={2}>
                      No features detected or added yet. Use "Analyze Image" or "Add Feature".
                    </Text>
                  </Flex>
                )}
              </VStack>
            </CardBody>
          </Card>
        </Box>
      )}
    </Flex>
  );
};

export default MediaPreviewSection;
